import { useMemo } from 'react'

import {
  matchFilterBetweenDates, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'
import { matchFilterBrand } from '@functions/invoice'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { InvoiceModel, InvoiceRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const isCreditorOrDebtor = (invoice: InvoiceModel, agencyId?: number) => {
  const {
    creditor_id, creditor_type, debtor_id, debtor_type,
  } = invoice

  if (!agencyId) return true
  if (creditor_type === 'Agency' && creditor_id === agencyId) return true
  if (debtor_type === 'Agency' && debtor_id === agencyId) return true

  return false
}

const watchEntityKeys = ['invoices']

type InvoiceFilters = {
  agency_id?: number,
  approved?: 'true' | 'false',
  brand_id?: number,
  campaign_id?: number,
  creditor_type?: 'Agency' | 'Brand' | 'Provider' | 'System',
  debtor_id?: number,
  debtor_type?: 'Agency' | 'Brand' | 'Provider',
  end_date?: string,
  paid?: boolean,
  start_date?: string,
  string?: string,
  title?: string,
  void?: 'true' | 'false',
}

type UseInvoicesOptions = {
  filters?: InvoiceFilters,
  performHttpRequests?: boolean,
  requestOptions?: InvoiceRequestOptions,
}

function useInvoices(options: UseInvoicesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    approved: filterIsApproved,
    brand_id: filterBrandId,
    campaign_id: filterCampaignId,
    creditor_type: filterCreditorType,
    debtor_id: filterDebtorId,
    debtor_type: filterDebtorType,
    end_date: filterCreatedBefore,
    paid: filterPaid,
    start_date: filterCreatedAfter,
    string: filterString,
    title: filterTitle,
    void: filterIsVoid,
  } = filters

  const {
    updatedEntities: { invoices: invoicesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agencies, campaigns, invoices } = useSelector(reduxState => reduxState.entities)

  const filteredInvoices = useMemo(() => {
    const filtered = Object.values(invoices).filter((invoice: InvoiceModel) => {
      const {
        approved,
        campaign_id,
        creditor_type,
        dates,
        debtor_id,
        debtor_type,
        id,
        item_ids,
        order_title,
        paid,
        reference,
        void: isVoid,
      } = invoice

      // @ts-expect-error TS2538
      const { agency_id } = campaigns[campaign_id] || {}

      // @ts-expect-error TS2339
      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchCampaign = matchFilterNumber(campaign_id, filterCampaignId)
      const matchAgency = matchFilterNumber(agency_id, filterAgencyId) && isCreditorOrDebtor(invoice, filterAgencyId)

      const matchId = id.toString() === filterString

      const matchInvoiceItemId = item_ids.some(itemId => itemId.toString() === filterString)

      const matchReference = matchFilterString(reference, filterString?.toString())
      const matchString = matchId || matchReference || matchInvoiceItemId

      const matchBrand = matchFilterBrand(invoice, agencies, filterBrandId)

      const matchCreditorType = filterCreditorType ? creditor_type === filterCreditorType : true

      const matchDebtorId = matchFilterNumber(debtor_id, filterDebtorId)
      const matchDebtorType = filterDebtorType ? debtor_type === filterDebtorType : true
      const matchDebtor = filterDebtorType && filterDebtorId
        ? matchDebtorId && matchDebtorType
        : true

      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedAfter, filterCreatedBefore)
      const matchPaid = matchFilterString(paid.toString(), filterPaid?.toString())
      const matchTitle = matchFilterString(order_title, filterTitle)
      const matchIsApproved = matchFilterString(approved.toString(), filterIsApproved, { exact: true })

      const matchIsVoid = matchFilterString(isVoid.toString(), filterIsVoid, { exact: true })

      return matchAgency
        && matchBrand
        && matchCampaign
        && matchCreatedAt
        && matchCreditorType
        && matchDebtor
        && matchDebtorType
        && matchIsApproved
        && matchIsVoid
        && matchPaid
        && matchString
        && matchTitle
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [invoicesUpdatedAt, JSON.stringify(filters)])

  const filteredInvoicesCount = filteredInvoices.length
  const hasFilteredInvoices = !!filteredInvoicesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredInvoicesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingInvoices } = useReduxAction(
    'invoices',
    'loadInvoices',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredInvoices,
    filteredInvoicesCount,
    hasFilteredInvoices,
    loading: loadingInvoices,
  }
}

export default useInvoices
